/**
 * Deferred Media Loader
 *
 * Hydrates product card sliders lazily:
 * - [data-product-slideshow]: desktop auto-cycling slideshow on hover
 * - [data-hover-slider]: touch swipe slider on mobile
 *
 * Both are observed regardless of device, since some devices match both
 * (hover: hover) and (pointer: coarse). Hydration happens on viewport
 * intersection, with a touchstart fallback for cases where the
 * IntersectionObserver callback never fires (Android Chrome power-saver,
 * background tabs, etc.).
 */

(function () {
  'use strict';

  // Parsed media data, keyed by the product-item element
  var mediaCache = new WeakMap();
  // Product items that already have the touchstart fallback bound
  var touchBound = new WeakSet();

  function escapeAttr(str) {
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function buildDesktopSlide(media) {
    var slide = document.createElement('div');
    slide.className = 'product-item__slideshow-slide';
    slide.setAttribute('data-slideshow-slide', '');
    slide.innerHTML = buildImageFigure(media);
    return slide;
  }

  function buildMobileSlide(media, index) {
    var slide = document.createElement('div');
    slide.className = 'product-item__bg__slide';
    if (index > 0) slide.setAttribute('data-hover-slide', '');
    slide.setAttribute('data-hover-slide-touch', '');

    if (media.type === 'image') {
      slide.innerHTML = buildImageFigure(media);
    } else if (media.html) {
      slide.innerHTML = media.html;
    }
    return slide;
  }

  function buildImageFigure(media) {
    return '<figure class="image-wrapper image-wrapper--cover lazy-image" style="--aspect-ratio: 0.6667;">' +
      '<img src="' + media.src + '" srcset="' + media.srcset + '" sizes="(min-width: 990px) 25vw, 50vw" alt="' + escapeAttr(media.alt || '') + '" loading="lazy" class="fit-cover">' +
      '</figure>';
  }

  function isDesktopSlideshow(container) {
    return container.matches('[data-product-slideshow]');
  }

  function hydrate(container) {
    if (container.getAttribute('data-media-hydrated') === 'true') return;

    var productItem = container.closest('.product-item') || container.closest('.grid-item');
    if (!productItem) return;

    var mediaItems = mediaCache.get(productItem);
    if (!mediaItems) return;

    var desktopShow = isDesktopSlideshow(container);
    var progressBar = container.querySelector('[data-slideshow-progress]');

    mediaItems.forEach(function (media, i) {
      if (media.type !== 'image' && !media.html) return;

      var slide = desktopShow ? buildDesktopSlide(media) : buildMobileSlide(media, i);

      if (progressBar) {
        container.insertBefore(slide, progressBar);
      } else {
        container.appendChild(slide);
      }
    });

    container.setAttribute('data-media-hydrated', 'true');

    // Allow slideshow scripts to reinitialize on this container
    container.removeAttribute('data-slideshow-init');

    // Reinitialize hover-images (touch slider) if this is inside one
    var hoverImagesEl = container.closest('hover-images');
    if (hoverImagesEl) {
      hoverImagesEl.classList.add('is-hydrated');
      if (typeof hoverImagesEl.initBasedOnDevice === 'function') {
        hoverImagesEl.initBasedOnDevice();
      }
    }
  }

  function dehydrate(container) {
    if (container.getAttribute('data-media-hydrated') !== 'true') return;

    var desktopShow = isDesktopSlideshow(container);
    var selector = desktopShow ? '[data-slideshow-slide]' : '[data-hover-slide-touch]';
    var slides = container.querySelectorAll(selector);
    slides.forEach(function (slide) {
      slide.remove();
    });

    container.removeAttribute('data-media-hydrated');
    container.removeAttribute('data-slideshow-init');

    var hoverImagesEl = container.closest('hover-images');
    if (hoverImagesEl) {
      hoverImagesEl.classList.remove('is-hydrated');
      if (hoverImagesEl.slider && hoverImagesEl.handleScroll) {
        hoverImagesEl.slider.removeEventListener('scroll', hoverImagesEl.handleScroll);
      }
    }
  }

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        hydrate(entry.target);
      } else {
        dehydrate(entry.target);
      }
    });
  }, {
    rootMargin: '200px 0px'
  });

  // Fallback: if IO never fires (Android power-saver, etc.), force-hydrate
  // when the user touches the card. One-shot, passive, no preventDefault —
  // tap-to-navigate and swipe-to-scroll both still work.
  function bindTouchFallback(productItem) {
    if (touchBound.has(productItem)) return;
    touchBound.add(productItem);

    productItem.addEventListener('touchstart', function () {
      var containers = productItem.querySelectorAll('[data-product-slideshow], [data-hover-slider]');
      containers.forEach(hydrate);
    }, { passive: true, once: true });
  }

  function registerContainers(root) {
    var containers = (root || document).querySelectorAll(
      '[data-product-slideshow][data-deferred-media], [data-hover-slider][data-deferred-media]'
    );

    containers.forEach(function (container) {
      var jsonStr = container.getAttribute('data-deferred-media');
      if (!jsonStr) return;

      var mediaItems;
      try {
        mediaItems = JSON.parse(jsonStr);
      } catch (e) {
        return;
      }

      if (!mediaItems || !mediaItems.length) return;

      var productItem = container.closest('.product-item') || container.closest('.grid-item');
      if (productItem) {
        mediaCache.set(productItem, mediaItems);
        bindTouchFallback(productItem);
      }

      container.removeAttribute('data-deferred-media');
      observer.observe(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { registerContainers(); });
  } else {
    registerContainers();
  }

  document.addEventListener('load-more:complete', function () { registerContainers(); });
})();
